import React, { useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  type ScrollViewInstance,
  StyleSheet,
  type TextInputInstance,
  View,
  type ViewInstance,
} from 'react-native';

import { theme } from '@garnish/constants';
import { useKeyboardListener } from '@garnish/core';
import { TextField } from '@garnish/forms';

export const AddressFormCustomNameField = (props: AddressFormCustomNameFieldProps) => {
  const { name, scrollViewRef, customNameNotice, isCustomNameInvalid, onChangeText } = props;

  const { formatMessage } = useIntl();

  // ─── Keyboard Avoidance Scroll ───────────────────────────────────────

  const inputRef = useRef<TextInputInstance>(null);
  const customNameRef = useRef<ViewInstance>(null);
  const { openKeyboardHeight } = useKeyboardListener();

  useEffect(() => {
    if (!inputRef.current?.isFocused()) return;

    const customNameNode = customNameRef.current;
    const scrollViewNode = scrollViewRef.current?.getNativeScrollRef();

    if (!customNameNode || !scrollViewNode) return;

    customNameNode.measureLayout(scrollViewNode, (_x, y) => {
      scrollViewRef.current?.scrollTo({
        y,
        animated: true,
      });
    });
  }, [openKeyboardHeight, scrollViewRef]);

  // Auto focus isn't working on iOS 18+
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <View ref={customNameRef} style={styles.customName}>
      <TextField
        autoFocus
        ref={inputRef}
        testID="account.address-form.custom-name"
        accessibilityLabel={formatMessage(messages.customNameLabel)}
        accessibilityHint={formatMessage(messages.customNameLabel)}
        label={formatMessage(messages.customNameLabel)}
        maxLength={20}
        value={name}
        invalid={isCustomNameInvalid}
        notice={customNameNotice}
        onChangeText={onChangeText}
      />
    </View>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  customNameLabel: {
    defaultMessage: 'Custom Name',
    description: 'Address Form | Custom Name Field | Label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type AddressFormCustomNameFieldProps = {
  name?: string;
  scrollViewRef: React.RefObject<ScrollViewInstance | null>;
  customNameNotice?: string;
  isCustomNameInvalid?: boolean;
  onChangeText: (value: string) => void;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  customName: { paddingTop: theme.spacing['4'] },
});
